import { useEffect, useState, type FormEvent } from 'react';
import { previewInvite, redeemInvite, type InvitePreview } from '../../api/accounts';
import { ApiError } from '../../api/client';
import { useLocale, useT } from '../../i18n/i18n.tsx';
import { PASSWORD_MIN } from '../account/profile.ts';
import { INVITABLE_ROLES, redeemFormError, windowLabel } from '../admin/invites';
import { Button, InlineAlert, Spinner, TextField } from '../../ui';
import './LoginDialog.css';
import { apiErrorMessage } from '../../api/errorMessage.ts';

/**
 * MVP-226: `/invite/<token>` — the one page in this app a person without an account can use to get
 * one. Signed out by definition, and mounted in `App.tsx` BEFORE the auth branch for the same
 * reason the Stage and guest routes are: an operator's own JWT sitting in this browser must not
 * turn someone else's invite link into the console.
 *
 * Deliberately reuses `LoginDialog.css` rather than growing a second stylesheet for the same
 * centred-card shape — this screen exists for about ninety seconds per account, once.
 *
 * The invite decides the role and the validity window; this form only collects a username, an
 * email and a password. There is nothing here to raise either, and the server would ignore it if
 * there were (`invite.redeem` reads both off the invite row).
 */
export function InviteRedeemScreen({ token }: { token: string }) {
  const t = useT();
  const { locale } = useLocale();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fields, setFields] = useState({ username: '', email: '', password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    previewInvite(token)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Unknown, already used and revoked all come back as the same 404 on purpose (the server
        // refuses to be an oracle), so `auth.invite.linkInvalid` covers all three honestly.
        setLoadError(
          err instanceof ApiError && err.status === 404
            ? t('auth.invite.linkInvalid')
            : t('auth.invite.checkFailed')
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    const problem = redeemFormError(fields);
    if (problem) {
      setError(t(problem));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const account = await redeemInvite(token, {
        username: fields.username.trim(),
        email: fields.email.trim(),
        password: fields.password,
      });
      setDone(account.username);
    } catch (err) {
      setError(apiErrorMessage(err, t('auth.invite.createFailed'), t));
    } finally {
      setSubmitting(false);
    }
  }

  const role = INVITABLE_ROLES.find((r) => r.value === preview?.roleType);
  // MVP-291: `admin`, not `audience`, even though whoever opens an invite link is not signed in
  // yet. What this line states is an ACCOUNT WINDOW — the same bound the Accounts screen and the
  // audit log print — and the person who sent the link reads it in the console. Two calendars for
  // one date is how a support call starts.
  const granted = preview ? windowLabel(preview, locale) : null;

  return (
    <main className="login-screen">
      <section className="login-dialog" aria-labelledby="invite-title">
        <div className="login-dialog__heading">
          <img className="login-dialog__logo" src="/logo-rms.png" alt={t('brand.logoAlt')} width={456} height={194} />
          <h1 id="invite-title" className="login-dialog__title text-title">
            RMS Karaoke
          </h1>
          <p className="login-dialog__subtitle text-body">
            {done ? t('auth.invite.subtitleDone') : t('auth.invite.subtitle')}
          </p>
        </div>

        {loadError && <InlineAlert variant="danger">{loadError}</InlineAlert>}

        {done && (
          <>
            <InlineAlert variant="ok">{t('auth.invite.created', { name: done })}</InlineAlert>
            <Button variant="primary" size="lg" fullWidth onClick={() => window.location.assign('/')}>
              {t('auth.invite.goToSignIn')}
            </Button>
          </>
        )}

        {!done && !loadError && !preview && (
          <p className="text-meta">
            <Spinner label={t('common.loading')} /> {t('auth.invite.checking')}
          </p>
        )}

        {!done && preview && (
          <>
            <p className="text-meta">
              {role ? `${t('auth.redeem.joiningAs', { role: t(role.labelKey) })} ` : ''}
              {granted?.key === 'admin.window.permanent'
                ? t('auth.redeem.accessPermanent')
                : t('auth.redeem.access', { window: granted ? t(granted.key, granted.params) : '' })}
            </p>
            {error && <InlineAlert variant="danger">{error}</InlineAlert>}
            <form className="login-dialog__form" onSubmit={handleSubmit}>
              <TextField
                label={t('auth.invite.username')}
                name="username"
                autoComplete="username"
                autoFocus
                required
                value={fields.username}
                onChange={(e) => setFields((f) => ({ ...f, username: e.target.value }))}
              />
              <TextField
                label={t('auth.invite.email')}
                name="email"
                type="email"
                autoComplete="email"
                required
                value={fields.email}
                onChange={(e) => setFields((f) => ({ ...f, email: e.target.value }))}
              />
              <TextField
                label={t('auth.invite.password')}
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN}
                placeholder={t('auth.passwordMinHint', { min: PASSWORD_MIN })}
                value={fields.password}
                onChange={(e) => setFields((f) => ({ ...f, password: e.target.value }))}
              />
              <TextField
                label={t('auth.invite.passwordAgain')}
                name="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={fields.confirm}
                onChange={(e) => setFields((f) => ({ ...f, confirm: e.target.value }))}
              />
              <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
                {t('auth.invite.submit')}
              </Button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
